/**
 * Gets keyboard-focusable elements within a specified element
 * @from https://github.com/zellwk/zellwk.com/blob/master/src/posts/2020-01-29-getting-focusable-elements.md
 *
 * @param {HTMLElement} [element=document] element
 * @returns {Array}
 */
export const getFocusableNode = (element = document) => {
  return [
    ...element.querySelectorAll(
      'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
    ),
  ].filter(
    el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
  )
}

/**
 *
 * @param {HTMLElement} container
 * @param {HTMLElement} element
 * @returns boolean
 */
export const isFirstFocusableNode = (container, element) => {
  const nodes = getFocusableNode(container)
  return nodes.findIndex(value => value === element) === 0
}

/**
 *
 * @param {HTMLElement} container
 * @param {HTMLElement} element
 * @returns boolean
 */
export const isLastFocusableNode = (container, element) => {
  const nodes = getFocusableNode(container)
  return nodes.findIndex(value => value === element) === nodes.length - 1
}

/**
 *
 * @param {Event} e
 * @returns boolean
 */
export const isKeyboardEventTriggeredByInput = e => {
  const targetTagName = e.target && e.target.tagName
  if (!targetTagName) return
  return (
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTagName) ||
    e.target.isContentEditable
  )
}

/**
 *
 * @param {HTMLElement} container
 */
export const focusFirstNode = (container, preventScroll = true) => {
  const nodes = getFocusableNode(container)

  if (nodes.length > 0) {
    nodes[0].focus({ preventScroll })
    return true
  }
  return false
}

/**
 *
 * @param {HTMLElement} container
 */
export const focusLastNode = container => {
  const nodes = getFocusableNode(container)
  if (nodes.length > 0) {
    nodes[nodes.length - 1].focus({ preventScroll: true })
    return true
  }
  return false
}

/**
 * Focuses the page's main landmark. A main without a tabindex gets
 * tabindex="-1" until it loses focus, so it does not join the tab order.
 */
export const focusMainContent = () => {
  const main = document.querySelector('main, [role="main"]')
  if (!main) return

  if (!main.hasAttribute('tabindex')) {
    main.setAttribute('tabindex', '-1')
    main.addEventListener('blur', () => main.removeAttribute('tabindex'), {
      once: true,
    })
  }
  main.focus({ preventScroll: true })
}
